'use client';

import { type MouseEventHandler, cloneElement, forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useToast } from './hooks/use-toast';
import {
  toastProviderItemContentStyles,
  toastProviderItemStyles,
} from './styles/toast-provider-item.styles';
import type { ToastProviderItemPropsInterface } from './types/toast-provider-item.types';

/**
 * ToastProvider.Item ingests a single toast object, and attaches toast queue event handlers
 * This component should be used within ToastProvider.List's `renderToast` prop
 */
export const ToastProviderItem = forwardRef<HTMLLIElement, ToastProviderItemPropsInterface>(
  (props: ToastProviderItemPropsInterface, forwardedRef) => {
    const { className, toast, css: cssProp, ...rest } = props;

    const context = useToast();
    const itemStyles = css(toastProviderItemStyles, cssProp);
    const itemContentStyles = css(toastProviderItemContentStyles);

    // Clones the <Toast /> element passed to enqueueToast()
    // and attaches event handlers from useToastQueue
    const content = useMemo(() => {
      if (!toast || !toast.node) return null;

      const { node, id } = toast;

      const onClose = () => {
        node.props.onClose?.();
        context.dismiss(id);
      };

      const onMouseEnter: MouseEventHandler<HTMLDivElement> = (e) => {
        node.props.onMouseEnter?.(e);
        if (context.pauseOnMouseEnter) {
          context.pause(id);
        }
      };

      const onMouseLeave: MouseEventHandler<HTMLDivElement> = (e) => {
        node.props.onMouseLeave?.(e);
        if (context.resumeOnMouseLeave) {
          context.resume(id);
        }
      };

      return cloneElement(node, {
        // Handles the Toast in controlled mode for exit animations
        // We don't want the Toast closing on its own before it is removed from the queue
        isOpen: true,
        onClose,
        onMouseEnter,
        onMouseLeave,
      });
    }, [context, toast]);

    return (
      <li
        ref={forwardedRef}
        className={cx('c11n', itemStyles, className)}
        data-c11n-component="ToastProvider.Item"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
      >
        <div className={cx('c11n', itemContentStyles)}>{content}</div>
      </li>
    );
  },
);

ToastProviderItem.displayName = 'ToastProvider.Item';
